import Big from 'big.js';
import {
  executeMultipleTransactions,
  LP_STORAGE_AMOUNT,
  ONE_YOCTO_NEAR,
  refFiViewFunction,
  REF_FI_CONTRACT_ID,
  Transaction,
  RefFiFunctionCallOptions,
  BLACKLIST_POOL_IDS,
  STABLE_POOL_ID,
  POOL_TOKEN_REFRESH_INTERVAL,
  ALL_STABLE_POOL_IDS,
  isStablePool,
  isRatedPool,
  filterBlackListPools,
} from './near';
import db from '../store/RefDatabase';
import {
  ftGetStorageBalance,
  TokenMetadata,
  native_usdc_has_upgraded,
  tokenFtMetadata,
} from './ft-contract';
import {
  toNonDivisibleNumber,
  toReadableNumber,
  ONLY_ZEROS,
} from '../utils/numbers';
import {
  storageDepositAction,
  storageDepositForFTAction,
  STORAGE_TO_REGISTER_WITH_FT,
} from './creators/storage';
import { getTopPoolsIndexerRaw } from '../services/indexer';
import { PoolRPCView } from './api';
import {
  checkTokenNeedsStorageDeposit,
  getDepositTransactions,
} from '../services/token';
import { getCurrentWallet } from '../utils/wallets-integration';
import { withdrawAction, registerAccountOnToken } from './creators/token';
import { getExplorer, ExplorerType } from '../utils/device';
import moment from 'moment';
import { getAllTriPools } from './aurora/aurora';
import { WRAP_NEAR_CONTRACT_ID, nearDepositTransaction } from './wrap-near';
import { STABLE_LP_TOKEN_DECIMALS } from '../components/stableswap/AddLiquidity';
import { getStablePoolDecimal } from 'src/utils/stablePoolFormatting';

import { cacheAllDCLPools } from './swapV3';
import getConfigV2 from '../services/configV2';
import { DEFLATION_MARK } from '../utils/token';
import { REF_DCL_POOL_CACHE_KEY } from '../state/swapHooks/swapStateShared';
const { NO_REQUIRED_REGISTRATION_TOKEN_IDS } = getConfigV2();

const explorerType = getExplorer();
export const DEFAULT_PAGE_LIMIT = 500;
const getStablePoolKey = (id: string) => `STABLE_POOL_VALUE_${id}`;

const getStablePoolInfoKey = (id: string) =>
  `REF_FI_STABLE_Pool_INFO_VALUE_${id}`;

export interface Pool {
  id: number;
  tokenIds: string[];
  supplies: { [key: string]: string };
  fee: number;
  shareSupply: string;
  tvl: number;
  token0_ref_price: string;
  partialAmountIn?: string;
  Dex?: string;
  rates?: {
    [id: string]: string;
  };
  pool_kind?: string;
  pairAdd?: string;
  metas?: {
    [id: string]: TokenMetadata;
  };
}

export interface StablePool {
  id: number;
  token_account_ids: string[];
  decimals: number[];
  amounts: string[];
  c_amounts: string[];
  total_fee: number;
  shares_total_supply: string;
  amp: number;
  rates: string[];
}

export const parsePool = (pool: PoolRPCView, id?: number): Pool => ({
  id: Number(id >= 0 ? id : pool.id),
  tokenIds: pool.token_account_ids,
  supplies: pool.amounts.reduce(
    (
      suppliesByTokenId: { [tokenId: string]: string },
      amount: string,
      index: number
    ) => {
      suppliesByTokenId[pool.token_account_ids[index]] = amount;
      return suppliesByTokenId;
    },
    {}
  ),
  fee: pool.total_fee,
  shareSupply: pool.shares_total_supply,
  tvl: pool.tvl,
  token0_ref_price: pool.token0_ref_price,
  pool_kind: pool?.pool_kind,
});

export const parsePoolNew = (pool: any, id?: number): any => {
  return {
    id: Number(id >= 0 ? id : pool.id),
    tokenIds: pool.token_account_ids,
    supplies: pool.amounts.reduce(
      (
        suppliesByTokenId: { [tokenId: string]: string },
        amount: string,
        index: number
      ) => {
        suppliesByTokenId[pool.token_account_ids[index]] = amount;
        return suppliesByTokenId;
      },
      {}
    ),
    fee: pool.total_fee,
    shareSupply: pool.shares_total_supply,
    tvl: pool.tvl,
    token0_ref_price: pool.token0_ref_price,
    pool_kind: pool?.pool_kind,
    apy: pool.apy,
    farm_apy: pool.farm_apy,
    is_farm: pool.is_farm,
    volume_24h: pool.volume_24h,
    token_symbols: pool.token_symbols,
    search_symbols: pool.token_symbols.join('-'),
    top: pool.top,
  };
};

export const getPoolsFromCache = async ({
  page = 1,
  perPage = DEFAULT_PAGE_LIMIT,
  tokenName = '',
  column = '',
  order = 'desc',
  uniquePairName = false,
}: {
  page?: number;
  perPage?: number;
  tokenName?: string;
  column?: string;
  order?: string;
  uniquePairName?: boolean;
}) => {
  const rows = await db.queryPools({
    page,
    perPage,
    tokenName,
    column,
    order,
    uniquePairName,
  });
  return rows.map((row) => ({
    id: row.id,
    tokenIds: [row.token1Id, row.token2Id],
    supplies: {
      [row.token1Id]: row.token1Supply,
      [row.token2Id]: row.token2Supply,
    },
    fee: row.fee,
    shareSupply: row.shares,
    tvl: 0,
    token0_ref_price: '0',
  }));
};

export const getAllWatchListFromDb = async ({
  account = getCurrentWallet()?.wallet?.getAccountId(),
}: {
  account?: string;
}) => {
  return await db
    .allWatchList()
    .where({
      account,
    })
    .toArray();
};

export const getWatchListFromDb = async ({
  pool_id,
  account = getCurrentWallet()?.wallet?.getAccountId(),
}: {
  pool_id: string;
  account?: string;
}) => {
  return await db
    .allWatchList()
    .where({
      pool_id,
      account,
    })
    .toArray();
};

export const addPoolToWatchList = async ({
  pool_id,
  account = getCurrentWallet()?.wallet?.getAccountId(),
}: {
  pool_id: string;
  account?: string;
}) => {
  return await db.watchList.put({
    id: account + '-' + pool_id,
    pool_id,
    account,
    update_time: new Date().getTime(),
  });
};
export const removePoolFromWatchList = async ({
  pool_id,
  account = getCurrentWallet()?.wallet?.getAccountId(),
}: {
  pool_id: string;
  account?: string;
}) => {
  return await db.watchList.delete(account + '-' + pool_id);
};

export const WATCHLIST_CHANGED = 'watchlist-changed';

export const notifyWatchList = () => {
  window.dispatchEvent(new Event(WATCHLIST_CHANGED));
};

export const getTotalPools = async () => {
  return refFiViewFunction({
    methodName: 'get_number_of_pools',
  });
};

const getAllPools = async (
  page: number = 1,
  perPage: number = DEFAULT_PAGE_LIMIT
): Promise<Pool[]> => {
  const index = (page - 1) * perPage;

  const poolData: PoolRPCView[] = await refFiViewFunction({
    methodName: 'get_pools',
    args: { from_index: index, limit: perPage },
  });

  return poolData.map((rawPool, pageOffset) =>
    parsePool(rawPool, pageOffset + index)
  );
};

interface GetPoolOptions {
  tokenInId: string;
  tokenOutId: string;
  amountIn?: string;
  setLoadingTrigger?: (loadingTrigger: boolean) => void;
  setLoadingData?: (loading: boolean) => void;
  loadingTrigger: boolean;
  crossSwap?: boolean;
  tokenIn?: TokenMetadata;
  tokenOut?: TokenMetadata;
  proGetCachePool?: boolean;
}

export const isNotStablePool = (pool: Pool) => {
  return !isStablePool(pool.id);
};

const fetchPoolsRPC = async () => {
  const totalPools = await getTotalPools();
  const pages = Math.ceil(totalPools / DEFAULT_PAGE_LIMIT);

  const allPools = (
    await Promise.all(
      [...Array(pages)].map((_, index) => getAllPools(index + 1))
    )
  )
    .flat()
    .map((pool) => ({ ...pool, Dex: 'ref' }));

  return allPools;
};

const fetchPoolsIndexer = async () => {
  return await getTopPoolsIndexerRaw();
};

const REF_FI_POOL_PROTOCOL = 'REF_FI_POOL_PROTOCOL_KEY';

const fetchTopPools = async () => {
  try {
    return {
      pools: await getTopPoolsIndexerRaw(),
      protocol: 'indexer',
    };
  } catch (error) {
    sessionStorage.setItem(REF_FI_POOL_PROTOCOL, 'rpc');

    await db.topPools.clear();

    await db.poolsTokens.clear();

    const rpcPools = await fetchPoolsRPC();

    return {
      pools: rpcPools,
      protocol: 'rpc',
    };
  }
};

export const getPoolsByTokens = async ({
  tokenInId,
  tokenOutId,
  setLoadingData,
  loadingTrigger,
  proGetCachePool,
  tokenIn,
  tokenOut,
}: GetPoolOptions): Promise<{
  filteredPools: Pool[];
  pool_protocol: string;
}> => {
  let pools;

  let pool_protocol = 'indexer';

  if (loadingTrigger) {
    const { pools: poolsRaw, protocol } = await fetchTopPools();
    pool_protocol = protocol;

    if (protocol === 'indexer') {
      await db.cacheTopPools(poolsRaw);
      pools = poolsRaw.map((rawPool: any) => {
        return {
          ...parsePool(rawPool),
          Dex: 'ref',
        };
      });

      sessionStorage.setItem(REF_FI_POOL_PROTOCOL, 'indexer');
    } else {
      pools = poolsRaw;

      sessionStorage.setItem(REF_FI_POOL_PROTOCOL, 'rpc');
    }

    await cachePools(pools);

    await cacheAllDCLPools();
  } else {
    if (!localStorage.getItem(REF_DCL_POOL_CACHE_KEY)) {
      await cacheAllDCLPools();
    }

    const cachedPoolProtocol = sessionStorage.getItem(REF_FI_POOL_PROTOCOL);
    pool_protocol = cachedPoolProtocol || 'indexer';

    if (cachedPoolProtocol === 'rpc') {
      pools = await db.getPoolsByTokens(tokenInId, tokenOutId);

      if (!pools || pools.length === 0) {
        pools = await fetchPoolsRPC();
        await cachePools(pools);
      }
    } else {
      const poolsRaw = await db.queryTopPools();

      if (poolsRaw && poolsRaw?.length > 0 && cachedPoolProtocol !== 'rpc') {
        pools = poolsRaw.map((rawPool) => {
          const parsedP = parsePool({
            ...rawPool,
            share: rawPool.shares_total_supply,
            id: Number(rawPool.id),
            tvl: Number(rawPool.tvl),
          });

          return {
            ...parsedP,
            Dex: 'ref',
          };
        });
      } else {
        const poolsRaw = await fetchPoolsIndexer();

        await db.cacheTopPools(poolsRaw);

        pools = poolsRaw.map((rawPool: any) => {
          return {
            ...parsePool(rawPool),
            Dex: 'ref',
          };
        });

        await cachePools(pools);
      }
    }
  }

  const filteredPools = pools
    .filter(filterBlackListPools)
    .filter((pool: any) => {
      return (
        pool.tokenIds.includes(tokenInId) &&
        pool.tokenIds.includes(tokenOutId) &&
        isNotStablePool(pool)
      );
    });
  return { filteredPools, pool_protocol };
};
// for server
export const getAllPoolsByTokens = async (
  needCheckExpiration?: boolean
): Promise<{
  filteredPools: Pool[];
}> => {
  let pools;
  try {
    const cachedPoolProtocol =
      sessionStorage.getItem(REF_FI_POOL_PROTOCOL) || 'indexer';
    if (cachedPoolProtocol === 'rpc') {
      pools = await db.getAllPoolsTokens();
      const validCache = await db.checkPoolsTokens();
      if (
        !pools ||
        pools.length === 0 ||
        (needCheckExpiration && !validCache)
      ) {
        pools = await fetchPoolsRPC();
        await cachePools(pools);
      }
    } else {
      const poolsRaw = await db.queryTopPools();
      const validCache = await db.checkTopPools();
      if (!poolsRaw?.length || (needCheckExpiration && !validCache)) {
        const poolsRaw = await fetchPoolsIndexer();

        await db.cacheTopPools(poolsRaw);

        pools = poolsRaw.map((rawPool: any) => {
          return {
            ...parsePool(rawPool),
            Dex: 'ref',
          };
        });

        await cachePools(pools);
      } else {
        pools = poolsRaw.map((rawPool) => {
          const parsedP = parsePool({
            ...rawPool,
            share: rawPool.shares_total_supply,
            id: Number(rawPool.id),
            tvl: Number(rawPool.tvl),
          });

          return {
            ...parsedP,
            Dex: 'ref',
          };
        });
      }
    }
  } catch (error) {
    const { pools: poolsRaw, protocol } = await fetchTopPools();

    if (protocol === 'indexer') {
      await db.cacheTopPools(poolsRaw);
      pools = poolsRaw.map((rawPool: any) => {
        return {
          ...parsePool(rawPool),
          Dex: 'ref',
        };
      });
      sessionStorage.setItem(REF_FI_POOL_PROTOCOL, 'indexer');
    } else {
      pools = poolsRaw;
      sessionStorage.setItem(REF_FI_POOL_PROTOCOL, 'rpc');
    }
    await cachePools(pools);
    await cacheAllDCLPools();
  }
  const filteredPools = pools
    .filter(filterBlackListPools)
    .filter((pool: any) => {
      return isNotStablePool(pool);
    });
  return { filteredPools };
};

export const getPoolsByTokensAurora = async ({
  tokenInId,
  tokenOutId,
  setLoadingData,
  loadingTrigger,
  crossSwap,
  proGetCachePool,
  tokenIn,
  tokenOut,
}: GetPoolOptions): Promise<Pool[]> => {
  let filtered_pools;
  const [cacheForPair, cacheTimeLimit] = await db.checkPoolsByTokens(
    tokenInId,
    tokenOutId,
    true
  );

  const PAIR_NAME = [
    !tokenIn || tokenIn.id === WRAP_NEAR_CONTRACT_ID ? 'wNEAR' : tokenIn.symbol,
    !tokenOut || tokenOut.id === WRAP_NEAR_CONTRACT_ID
      ? 'wNEAR'
      : tokenOut.symbol,
  ].join('-');

  if ((!loadingTrigger && cacheTimeLimit) || !cacheForPair) {
    filtered_pools = await db.getPoolsByTokens(tokenInId, tokenOutId, true);

    let triPools: any = sessionStorage.getItem(`REF_FI_TRI_POOL_` + PAIR_NAME);
    if (triPools) {
      triPools = JSON.parse(triPools);
    } else {
      triPools = await getAllTriPools(
        tokenIn && tokenOut
          ? [
              tokenIn.id === WRAP_NEAR_CONTRACT_ID ? 'wNEAR' : tokenIn.symbol,
              tokenOut.id === WRAP_NEAR_CONTRACT_ID ? 'wNEAR' : tokenOut.symbol,
            ]
          : null
      );
    }
    filtered_pools = filtered_pools.concat(triPools || []);
  }
  if (
    (crossSwap && proGetCachePool) ||
    loadingTrigger ||
    (!cacheTimeLimit && cacheForPair)
  ) {
    setLoadingData && setLoadingData(true);

    const triPools = await getAllTriPools(
      tokenIn && tokenOut
        ? [
            tokenIn.id === WRAP_NEAR_CONTRACT_ID ? 'wNEAR' : tokenIn.symbol,
            tokenOut.id === WRAP_NEAR_CONTRACT_ID ? 'wNEAR' : tokenOut.symbol,
          ]
        : null
    );

    if (triPools && triPools.length > 0) {
      sessionStorage.setItem(
        `REF_FI_TRI_POOL_` + PAIR_NAME,
        JSON.stringify(triPools)
      );
    }

    filtered_pools = triPools;

    await db.cachePoolsByTokensAurora(filtered_pools);
    filtered_pools = filtered_pools.filter(
      (pool: any) => pool.supplies[tokenInId] && pool.supplies[tokenOutId]
    );
  }

  setLoadingData && setLoadingData(false);

  // @ts-ignore
  return filtered_pools;
};

export const getRefPoolsByToken1ORToken2 = async () => {
  return await db.queryPoolsByTokens2();
};

const getPool = async (id: number): Promise<Pool> => {
  return refFiViewFunction({
    methodName: 'get_pool',
    args: { pool_id: id },
  }).then((pool: PoolRPCView) => parsePool(pool, id));
};

interface PoolVolumes {
  [tokenId: string]: { input: string; output: string };
}

export type PoolDetails = Pool & { volumes: PoolVolumes };

export const getPoolDetails = async (id: number): Promise<PoolDetails> => {
  const [pool, volumes] = await Promise.all([
    getPool(id),
    refFiViewFunction({
      methodName: 'get_pool_volumes',
      args: { pool_id: id },
    }),
  ]);

  return {
    ...pool,
    volumes: pool.tokenIds.reduce(
      (volumesByTokenId: PoolVolumes, tokenId, index) => {
        volumesByTokenId[tokenId] = volumes[index];
        return volumesByTokenId;
      },
      {}
    ),
  };
};

export const getSharesInPool = (id: number): Promise<string> => {
  return refFiViewFunction({
    methodName: 'get_pool_shares',
    args: {
      pool_id: id,
      account_id: getCurrentWallet()?.wallet?.getAccountId(),
    },
  });
};

export const getFarmsCount = (poolId: string | number, farms: any) => {
  const count = farms.reduce((pre: number, farm: any) => {
    if (Number(farm.pool_id) === Number(poolId)) return pre + 1;
    return pre;
  }, 0);

  return count;
};

export const getEndedFarmsCount = (poolId: string | number, farms: any) => {
  const count = farms.reduce((pre: number, farm: any) => {
    if (
      Number(farm.pool_id) === Number(poolId) &&
      (farm.status === 'Ended' || farm.status === 'Created')
    )
      return pre + 1;
    return pre;
  }, 0);

  return count;
};
export const getRealEndedFarmsCount = (poolId: string | number, farms: any) => {
  const count = farms.reduce((pre: number, farm: any) => {
    if (Number(farm.pool_id) === Number(poolId) && farm.status === 'Ended')
      return pre + 1;
    return pre;
  }, 0);

  return count;
};

export const canFarm = async (
  _pool_id: number,
  _withEnded?: boolean
): Promise<Record<string, any>> => {
  return { count: 0, version: 'V2' };
};

export const canFarms = async ({
  pool_ids,
}: {
  pool_ids: number[];
  withEnded?: boolean;
}) => {
  return (pool_ids || []).reduce((counts, poolId) => {
    counts[poolId] = 0;
    return counts;
  }, {} as Record<string, number>);
};

export const canFarmV1 = async (
  _pool_id: number,
  _withEnded?: boolean
): Promise<Record<string, any>> => {
  return { count: 0, version: 'V1', endedCount: 0 };
};

export const canFarmV2 = async (
  _pool_id: number,
  _withEnded?: boolean
): Promise<Record<string, any>> => {
  return { count: 0, version: 'V2', endedCount: 0 };
};

interface AddLiquidityToPoolOptions {
  id: number;
  tokenAmounts: { token: TokenMetadata; amount: string }[];
}

export const addLiquidityToPool = async ({
  id,
  tokenAmounts,
}: AddLiquidityToPoolOptions) => {
  let amounts = tokenAmounts.map(({ token, amount }) =>
    toNonDivisibleNumber(token.decimals, amount)
  );

  const transactions = await getDepositTransactions({
    tokens: tokenAmounts.map(({ token, amount }) => token),
    amounts: tokenAmounts.map(({ token, amount }) => amount),
  });
  // add deflation calc logic
  const tknx_tokens = tokenAmounts
    .map((tokenAmount) => tokenAmount.token)
    .filter((token) => token.id.includes(DEFLATION_MARK));
  if (tknx_tokens.length > 0) {
    const pending = tknx_tokens.map((token) => tokenFtMetadata(token.id));
    const tokenFtMetadatas = await Promise.all(pending);
    const rate = tokenFtMetadatas.reduce((ratesByTokenId, metadata, index) => {
      const is_owner =
        metadata.owner_account_id == getCurrentWallet()?.wallet?.getAccountId();
      return {
        ...ratesByTokenId,
        [tknx_tokens[index].id]: is_owner
          ? 0
          : (metadata?.deflation_strategy?.fee_strategy?.SellFee?.fee_rate ??
              0) +
            (metadata?.deflation_strategy?.burn_strategy?.SellBurn?.burn_rate ??
              0),
      };
    }, {});
    amounts = tokenAmounts.map(({ token, amount }) => {
      const beforeAmount = toNonDivisibleNumber(token.decimals, amount);
      let afterAmount = beforeAmount;
      if (rate[token.id]) {
        afterAmount = Big(1 - rate[token.id] / 1000000)
          .mul(beforeAmount)
          .toFixed(0);
      }
      return afterAmount;
    });
  }
  const actions: RefFiFunctionCallOptions[] = [
    {
      methodName: 'add_liquidity',
      args: { pool_id: id, amounts },
      amount: LP_STORAGE_AMOUNT,
    },
  ];

  transactions.push({
    receiverId: REF_FI_CONTRACT_ID,
    functionCalls: [...actions],
  });

  const wNearTokenAmount = tokenAmounts.find(
    (tokenAmount) => tokenAmount.token.id === WRAP_NEAR_CONTRACT_ID
  );

  if (wNearTokenAmount && !ONLY_ZEROS.test(wNearTokenAmount.amount)) {
    transactions.unshift(nearDepositTransaction(wNearTokenAmount.amount));
  }

  if (
    tokenAmounts
      .map((tokenAmount) => tokenAmount.token.id)
      .includes(WRAP_NEAR_CONTRACT_ID)
  ) {
    const registered = await ftGetStorageBalance(WRAP_NEAR_CONTRACT_ID);
    if (registered === null) {
      transactions.unshift({
        receiverId: WRAP_NEAR_CONTRACT_ID,
        functionCalls: [registerAccountOnToken()],
      });
    }
  }

  return executeMultipleTransactions(transactions);
};

interface RemoveLiquidityOptions {
  id: number;
  shares: string;
  minimumAmounts: { [tokenId: string]: string };
  unregister?: boolean;
}

export const removeLiquidityFromPool = async ({
  id,
  shares,
  minimumAmounts,
  unregister = false,
}: RemoveLiquidityOptions) => {
  const pool = await getPool(id);

  const amounts = pool.tokenIds.map((tokenId) => minimumAmounts[tokenId]);

  const tokenIds = Object.keys(minimumAmounts);

  const withDrawTransactions: Transaction[] = [];

  for (let tokenIndex = 0; tokenIndex < tokenIds.length; tokenIndex++) {
    const tokenId = tokenIds[tokenIndex];

    const ftBalance = await ftGetStorageBalance(tokenId);
    if (ftBalance === null) {
      if (NO_REQUIRED_REGISTRATION_TOKEN_IDS.includes(tokenId)) {
        const hasUpgraded = await native_usdc_has_upgraded(tokenId);
        if (hasUpgraded) {
          withDrawTransactions.unshift({
            receiverId: tokenId,
            functionCalls: [
              storageDepositAction({
                registrationOnly: true,
                amount: STORAGE_TO_REGISTER_WITH_FT,
              }),
            ],
          });
        } else {
          withDrawTransactions.unshift({
            receiverId: tokenId,
            functionCalls: [
              {
                methodName: 'register_account',
                args: {
                  account_id: getCurrentWallet()?.wallet?.getAccountId(),
                },
                gas: '10000000000000',
              },
            ],
          });
        }
      } else {
        withDrawTransactions.unshift({
          receiverId: tokenId,
          functionCalls: [
            storageDepositAction({
              registrationOnly: true,
              amount: STORAGE_TO_REGISTER_WITH_FT,
            }),
          ],
        });
      }
    }
  }

  const neededStorage = await checkTokenNeedsStorageDeposit();
  if (neededStorage) {
    withDrawTransactions.unshift({
      receiverId: REF_FI_CONTRACT_ID,
      functionCalls: [storageDepositAction({ amount: neededStorage })],
    });
  }

  const withdrawActions = tokenIds.map((tokenId, index) =>
    withdrawAction({ tokenId, amount: '0', unregister })
  );

  const withdrawActionsFireFox = tokenIds.map((tokenId, index) =>
    withdrawAction({ tokenId, amount: '0', unregister, singleTx: true })
  );

  const actions: RefFiFunctionCallOptions[] = [
    {
      methodName: 'remove_liquidity',
      args: {
        pool_id: id,
        shares,
        min_amounts: amounts,
      },
      amount: ONE_YOCTO_NEAR,
      gas: '30000000000000',
    },
  ];
  if (explorerType !== ExplorerType.Firefox) {
    withdrawActions.forEach((action) => {
      actions.push(action);
    });
  }

  const transactions: Transaction[] = [
    ...withDrawTransactions,
    {
      receiverId: REF_FI_CONTRACT_ID,
      functionCalls: [...actions],
    },
  ];

  if (explorerType === ExplorerType.Firefox) {
    transactions.push({
      receiverId: REF_FI_CONTRACT_ID,
      functionCalls: withdrawActionsFireFox,
    });
  }

  // if (
  //   tokenIds.includes(WRAP_NEAR_CONTRACT_ID) &&
  //   !ONLY_ZEROS.test(minimumAmounts[WRAP_NEAR_CONTRACT_ID])
  // ) {
  //   transactions.push(
  //     nearWithdrawTransaction(
  //       toReadableNumber(
  //         nearMetadata.decimals,
  //         minimumAmounts[WRAP_NEAR_CONTRACT_ID]
  //       )
  //     )
  //   );
  // }

  return executeMultipleTransactions(transactions);
};

// todo by tokens

export const addSimpleLiquidityPool = async (
  tokenIds: string[],
  fee: number
) => {
  const storageBalances = await Promise.all(
    tokenIds.map((id) => ftGetStorageBalance(id, REF_FI_CONTRACT_ID))
  );

  const transactions: Transaction[] = storageBalances
    .reduce((unregisteredTokenIds, storageBalance, index) => {
      if (!storageBalance || storageBalance.total === '0')
        unregisteredTokenIds.push(tokenIds[index]);
      return unregisteredTokenIds;
    }, [])
    .map((id) => ({
      receiverId: id,
      functionCalls: [storageDepositForFTAction()],
    }));

  transactions.push({
    receiverId: REF_FI_CONTRACT_ID,
    functionCalls: [
      {
        methodName: 'add_simple_pool',
        args: { tokens: tokenIds, fee },
        amount: '0.05',
      },
    ],
  });

  return executeMultipleTransactions(
    transactions,
    `${window.location.origin}/pools/add`
  );
};

const getStablePool = async (pool_id: number): Promise<StablePool> => {
  if (isRatedPool(pool_id)) {
    const pool_info = await refFiViewFunction({
      methodName: 'get_rated_pool',
      args: { pool_id },
    });

    return {
      ...pool_info,
      id: pool_id,
    };
  }

  const pool_info = await refFiViewFunction({
    methodName: 'get_stable_pool',
    args: { pool_id },
  });

  return {
    ...pool_info,
    id: pool_id,
    rates: pool_info.c_amounts.map((amount: any) =>
      toNonDivisibleNumber(STABLE_LP_TOKEN_DECIMALS, '1')
    ),
  };
};

export const getStablePoolFromCache = async (id?: string) => {
  const stable_pool_id = id || STABLE_POOL_ID.toString();

  const pool_key = getStablePoolKey(stable_pool_id);

  const poolInfoKey = getStablePoolInfoKey(stable_pool_id);

  const stablePoolCache = JSON.parse(localStorage.getItem(pool_key));

  const stablePoolInfoCache = JSON.parse(localStorage.getItem(poolInfoKey));

  const isStablePoolCached =
    stablePoolCache?.update_time &&
    Number(stablePoolCache.update_time) >
      Number(moment().unix() - Number(POOL_TOKEN_REFRESH_INTERVAL));

  const isStablePoolInfoCached =
    stablePoolInfoCache?.update_time &&
    Number(stablePoolInfoCache.update_time) >
      Number(moment().unix() - Number(POOL_TOKEN_REFRESH_INTERVAL));

  const stablePool = isStablePoolCached
    ? stablePoolCache
    : await getPool(Number(stable_pool_id));

  const stablePoolInfo = isStablePoolInfoCached
    ? stablePoolInfoCache
    : await getStablePool(Number(stable_pool_id));

  if (!isStablePoolCached) {
    localStorage.setItem(
      pool_key,
      JSON.stringify({ ...stablePool, update_time: moment().unix() })
    );
  }

  if (!isStablePoolInfoCached) {
    localStorage.setItem(
      poolInfoKey,
      JSON.stringify({ ...stablePoolInfo, update_time: moment().unix() })
    );
  }
  stablePool.rates = stablePoolInfo.token_account_ids.reduce(
    (ratesByTokenId: any, tokenId: any, index: number) => ({
      ...ratesByTokenId,
      [tokenId]: toReadableNumber(
        getStablePoolDecimal(stablePool.id),
        stablePoolInfo.rates[index]
      ),
    }),
    {}
  );

  return [stablePool, stablePoolInfo];
};

export const getAllStablePoolsFromCache = async (loadingTrigger?: boolean) => {
  const poolPairs = await Promise.all(
    ALL_STABLE_POOL_IDS.filter((id) => {
      return !BLACKLIST_POOL_IDS.includes(id.toString());
    }).map((id) => getStablePoolFromCache(id.toString()))
  );

  const allStablePoolsById = poolPairs.reduce((pre, poolPair, index) => {
    return {
      ...pre,
      [poolPair[0].id]: poolPair,
    };
  }, {}) as {
    [id: string]: [Pool, StablePool];
  };
  const allStablePools = Object.values(allStablePoolsById).map(
    (poolPair) => poolPair[0]
  );
  const allStablePoolsInfo = Object.values(allStablePoolsById).map(
    (poolPair) => poolPair[1]
  );

  return {
    allStablePoolsById,
    allStablePools,
    allStablePoolsInfo,
  };
};

// for server router
export const getStablePoolFromCacheForServer = async (id: string) => {
  const stable_pool_id = id;

  const pool_key = getStablePoolKey(stable_pool_id);

  const poolInfoKey = getStablePoolInfoKey(stable_pool_id);

  const stablePoolCache = JSON.parse(localStorage.getItem(pool_key));

  const stablePoolInfoCache = JSON.parse(localStorage.getItem(poolInfoKey));

  const isStablePoolCached = !!stablePoolCache;

  const isStablePoolInfoCached = !!stablePoolInfoCache;

  const stablePool = isStablePoolCached
    ? stablePoolCache
    : await getPool(Number(stable_pool_id));

  const stablePoolInfo = isStablePoolInfoCached
    ? stablePoolInfoCache
    : await getStablePool(Number(stable_pool_id));

  if (!isStablePoolCached) {
    localStorage.setItem(
      pool_key,
      JSON.stringify({ ...stablePool, update_time: moment().unix() })
    );
  }

  if (!isStablePoolInfoCached) {
    localStorage.setItem(
      poolInfoKey,
      JSON.stringify({ ...stablePoolInfo, update_time: moment().unix() })
    );
  }
  stablePool.rates = stablePoolInfo.token_account_ids.reduce(
    (ratesByTokenId: any, tokenId: any, index: number) => ({
      ...ratesByTokenId,
      [tokenId]: toReadableNumber(
        getStablePoolDecimal(stablePool.id),
        stablePoolInfo.rates[index]
      ),
    }),
    {}
  );

  return [stablePool, stablePoolInfo];
};
export const getAllStablePoolsFromCacheForServer = async () => {
  const poolPairs = await Promise.all(
    ALL_STABLE_POOL_IDS.filter((id) => {
      return !BLACKLIST_POOL_IDS.includes(id.toString());
    }).map((id) => getStablePoolFromCacheForServer(id.toString()))
  );

  const allStablePoolsById = poolPairs.reduce((pre, poolPair, index) => {
    return {
      ...pre,
      [poolPair[0].id]: poolPair,
    };
  }, {}) as {
    [id: string]: [Pool, StablePool];
  };
  const allStablePools = Object.values(allStablePoolsById).map(
    (poolPair) => poolPair[0]
  );
  const allStablePoolsInfo = Object.values(allStablePoolsById).map(
    (poolPair) => poolPair[1]
  );

  return {
    allStablePoolsById,
    allStablePools,
    allStablePoolsInfo,
  };
};

const cachePools = async (pools: any) => {
  await db.cachePoolsByTokens(
    pools
      .filter(filterBlackListPools)
      .filter((pool: any) => isNotStablePool(pool))
  );
};
